import { useState, useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import { HomeHero } from '@layouts/HomeHero';
import { WelcomeSection } from '@layouts/WelcomeSection';
import { DashboardTabs } from '@/components/dashboard/DashboardTabs';
import { BookAnotherCard } from '@/components/dashboard/BookAnotherCard';
import { BookingsPagination } from '@/components/dashboard/BookingsPagination/BookingsPagination';
import { RequestList } from '@/components/dashboard/RequestList';
import { DeleteConfirmDialog } from '@/components/dashboard/DeleteConfirmDialog';
import { CancelRequestDialog } from '@/components/dashboard/CancelRequestDialog';
import { EventRequestDetails } from '@/components/dashboard/EventRequestDetails';
import { FloatingActionBar } from '@/components/common';
import { useBookingsPaginatedEvents } from '@/hooks/useBookingsPaginatedEvents/useBookingsPaginatedEvents';
import { useAnchorVisibility } from '@hooks/useAnchorVisibility';
import { ROUTES } from '@routes/routeConstants';
import { formatSeriesOrdinalTitle } from '@/utils/seriesEventTitle';
import { useBookingsDashboardStyles } from './BookingsDashboard.styles';
import type { DashboardTab, EventSummary } from '@/types/events.types';
import type { EventId } from '@/store/models/ids';
import { telemetry } from '@/services/telemetry';
import { getActiveAccount } from '@/services/token';
import { TELEMETRY_COMPONENT } from '@/constants';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  beginRequestAction,
  requestActionSucceeded,
  requestActionFailed,
  clearRequestAction,
  setSelectedEventId,
  selectRequestActionKind,
  selectRequestActionInFlight,
  selectRequestActionError,
  selectSelectedEventId,
} from '@store/slices';

export const BookingsScreen = () => {
  const styles = useBookingsDashboardStyles();
  const { t } = useTranslation(['homeScreen', 'dashboard']);

  useEffect(() => {
    telemetry.trackPageView(TELEMETRY_COMPONENT, 'BookingsScreen');
  }, []);

  // Reset the shared request-action flow when entering the screen so a stale
  // selection/error from a prior visit does not resurface (FR-016).
  useEffect(() => {
    dispatch(clearRequestAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const requestActionKind = useAppSelector(selectRequestActionKind);
  const requestActionInFlight = useAppSelector(selectRequestActionInFlight);
  const requestActionError = useAppSelector(selectRequestActionError);
  const selectedEventId = useAppSelector(selectSelectedEventId) as EventId | null;

  const [activeTab, setActiveTab] = useState<DashboardTab>('current');
  // Dialog visibility + the targeted event id are ephemeral dialog UI (FR-018).
  // The shared flow state (in-flight, error, kind) and the active selection live
  // in `requestActionsSlice` (FR-016). Deep-link cancellation may temporarily
  // hold a fetched target when the event is outside the loaded page cache.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetSeriesId, setDeleteTargetSeriesId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTargetEventId, setCancelTargetEventId] = useState<string | null>(null);
  const [deepLinkedCancelEvent, setDeepLinkedCancelEvent] = useState<EventSummary | null>(null);
  const deleteLoading = requestActionInFlight && requestActionKind === 'delete';
  const deleteError = requestActionKind === 'delete' ? requestActionError : null;
  const cancelLoading = requestActionInFlight && requestActionKind === 'cancel';
  const cancelError = requestActionKind === 'cancel' ? requestActionError : null;
  const handledCancelActionRef = useRef<string | null>(null);
  const pendingCancelActionRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const sectionHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const sectionHeadingId = useId();
  const heroCtaRef = useRef<HTMLSpanElement | null>(null);
  const { isAnchorVisible: isHeroCtaVisible } = useAnchorVisibility(heroCtaRef);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const {
    tabs,
    isInitialLoadComplete,
    setPage,
    retryTab,
    getEventForAction,
    deleteEvent,
    cancelEvent,
  } = useBookingsPaginatedEvents();

  const currentTabState = tabs[activeTab];
  const selectedEvents = currentTabState.pages[currentTabState.currentPage] ?? [];

  const allLoadedEvents = useMemo(
    () =>
      (Object.keys(tabs) as DashboardTab[]).flatMap((tab) =>
        Object.values(tabs[tab].pages).flatMap((events) => events)
      ),
    [tabs]
  );

  // Server data is read from the loaded-event cache by id, never held in local state (FR-015).
  const eventToDelete = useMemo(() => {
    if (!deleteTargetSeriesId) return null;
    const draftEvents = Object.values(tabs.draft.pages).flatMap((events) => events);
    return draftEvents.find((event) => event.seriesId === deleteTargetSeriesId) ?? null;
  }, [deleteTargetSeriesId, tabs.draft.pages]);

  const eventToCancel = useMemo(() => {
    if (!cancelTargetEventId) return null;
    return (
      (deepLinkedCancelEvent?.eventId === cancelTargetEventId ? deepLinkedCancelEvent : null) ??
      allLoadedEvents.find((event) => event.eventId === cancelTargetEventId)
    );
  }, [cancelTargetEventId, allLoadedEvents, deepLinkedCancelEvent]);
  const currentUserEmail = getActiveAccount()?.username.trim().toLowerCase();
  const isUnauthorizedOnSiteContact = useMemo(() => {
    if (!eventToCancel || !currentUserEmail) return false;

    const authorizedEmails = [
      eventToCancel.submittedByEmail,
      eventToCancel.createdBy,
      eventToCancel.sponsor?.email,
    ];
    const isAuthorized = authorizedEmails.some(
      (email) => email?.trim().toLowerCase() === currentUserEmail
    );
    const isOnSiteContact = eventToCancel.onSiteContacts?.some(
      (contact) => contact.email?.trim().toLowerCase() === currentUserEmail
    );

    return Boolean(isOnSiteContact && !isAuthorized);
  }, [currentUserEmail, eventToCancel]);
  const cancelDialogMessage =
    eventToCancel?.eventState === 'CANCELLED'
      ? t('dashboard:cancelDialog.alreadyCancelledMessage')
      : isUnauthorizedOnSiteContact
        ? t('dashboard:cancelDialog.unauthorizedMessage')
      : null;

  // The event-by-id detail endpoint does not carry series ordinal metadata, so
  // resolve the (index/total) suffix from the already-loaded list event and
  // hand it to the modal as its title override (spec 027 FR-004a).
  const selectedEventTitleOverride = useMemo(() => {
    if (!selectedEventId) return undefined;
    const selected = allLoadedEvents.find((event) => event.eventId === selectedEventId);
    const baseTitle = selected?.eventTitle;
    if (!baseTitle) return undefined;
    const suffixedTitle = formatSeriesOrdinalTitle(
      baseTitle,
      selected.seriesIndex,
      selected.seriesTotal
    );
    return suffixedTitle === baseTitle ? undefined : suffixedTitle;
  }, [selectedEventId, allLoadedEvents]);

  const countForTab = useCallback(
    (tab: DashboardTab) =>
      tabs[tab].totalCount ??
      Object.values(tabs[tab].pages).reduce((total, events) => total + events.length, 0),
    [tabs]
  );

  // Handle deep-link query params from email links (/events/:eventId → /bookings?eventId=...&action=cancel)
  useEffect(() => {
    const eventIdParam = searchParams.get('eventId');
    const actionParam = searchParams.get('action');
    if (!eventIdParam || !isInitialLoadComplete) return;

    if (actionParam === 'cancel') {
      const cancelActionKey = `${eventIdParam}:${actionParam}`;
      if (handledCancelActionRef.current === cancelActionKey) {
        return;
      }

      const listEvent = allLoadedEvents.find(
        (loadedEvent) => loadedEvent.eventId === eventIdParam
      );
      if (listEvent?.eventState === 'CANCELLED') {
        handledCancelActionRef.current = cancelActionKey;
        setDeepLinkedCancelEvent(null);
        setCancelTargetEventId(listEvent.eventId);
        dispatch(requestActionSucceeded());
        setCancelDialogOpen(true);
        return;
      }

      if (pendingCancelActionRef.current === cancelActionKey) {
        return;
      }

      pendingCancelActionRef.current = cancelActionKey;
      void getEventForAction(eventIdParam)
        .then((fetchedEvent) => {
          if (!mountedRef.current) return;
          handledCancelActionRef.current = cancelActionKey;
          setDeepLinkedCancelEvent({
            ...listEvent,
            ...fetchedEvent,
            seriesIndex: listEvent?.seriesIndex ?? fetchedEvent.seriesIndex,
            seriesTotal: listEvent?.seriesTotal ?? fetchedEvent.seriesTotal,
          });
          setCancelTargetEventId(fetchedEvent.eventId);
          dispatch(requestActionSucceeded());
          setCancelDialogOpen(true);
        })
        .catch((err: unknown) => {
          if (!mountedRef.current) return;
          const message =
            (err as Error)?.message || 'Unable to load event. Please refresh and try again.';
          dispatch(requestActionFailed(message));
        })
        .finally(() => {
          if (pendingCancelActionRef.current === cancelActionKey) {
            pendingCancelActionRef.current = null;
          }
        });
    } else {
      dispatch(setSelectedEventId(eventIdParam));
    }
  }, [allLoadedEvents, getEventForAction, isInitialLoadComplete, searchParams, dispatch]);

  const hasEvents =
    countForTab('draft') > 0 || countForTab('current') > 0 || countForTab('past') > 0;

  const viewTriggerRef = useRef<HTMLElement | null>(null);
  const cancelTriggerRef = useRef<HTMLElement | null>(null);

  const handleStartBooking = () => {
    void navigate(ROUTES.BOOKINGS_NEW);
  };

  const handleTabChange = useCallback((tab: DashboardTab) => {
    setActiveTab(tab);
  }, []);

  const handleView = useCallback(
    (eventId: string) => {
      // Store the trigger element so focus can be restored when the dialog closes
      viewTriggerRef.current = document.activeElement as HTMLElement | null;
      dispatch(setSelectedEventId(eventId));
      setSearchParams({ eventId }, { replace: true });
    },
    [setSearchParams, dispatch]
  );

  const handleDraftView = useCallback(
    (eventId: string) => {
      void navigate(`${ROUTES.BOOKINGS_NEW}?eventId=${eventId}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    (seriesId: string) => {
      const draftEvents = Object.values(tabs.draft.pages).flatMap((events) => events);
      const event = draftEvents.find((draftEvent) => draftEvent.seriesId === seriesId);
      if (event) {
        setDeleteTargetSeriesId(event.seriesId ?? null);
        dispatch(requestActionSucceeded());
        setDeleteDialogOpen(true);
      }
    },
    [tabs.draft.pages, dispatch]
  );

  const handleConfirmDelete = useCallback(() => {
    if (!eventToDelete) return;
    dispatch(beginRequestAction({ kind: 'delete', targetEventId: eventToDelete.eventId }));
    void deleteEvent(eventToDelete.seriesId ?? eventToDelete.eventId, eventToDelete.buildingId)
      .then(() => {
        setDeleteDialogOpen(false);
        setDeleteTargetSeriesId(null);
        dispatch(requestActionSucceeded());
      })
      .catch((err: Error) => {
        dispatch(requestActionFailed(err.message || 'Failed to delete draft. Please try again.'));
      });
  }, [eventToDelete, deleteEvent, dispatch]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteTargetSeriesId(null);
    dispatch(requestActionSucceeded());
  }, [dispatch]);

  const handleCancel = useCallback(
    (eventId: string) => {
      const event = allLoadedEvents.find((loadedEvent) => loadedEvent.eventId === eventId);
      if (event) {
        cancelTriggerRef.current = document.activeElement as HTMLElement | null;
        handledCancelActionRef.current = `${eventId}:cancel`;
        setDeepLinkedCancelEvent(null);
        setCancelTargetEventId(event.eventId);
        dispatch(requestActionSucceeded());
        setCancelDialogOpen(true);
        setSearchParams({ eventId, action: 'cancel' }, { replace: true });
      }
    },
    [allLoadedEvents, setSearchParams, dispatch]
  );

  const handleConfirmCancel = useCallback(
    (comments: string) => {
      if (!eventToCancel) return;
      const targetEventId = eventToCancel.eventId;
      dispatch(beginRequestAction({ kind: 'cancel', targetEventId }));
      void cancelEvent(eventToCancel.eventId, eventToCancel.buildingId, comments)
        .then(() => {
          setCancelDialogOpen(false);
          setCancelTargetEventId(null);
          setDeepLinkedCancelEvent(null);
          dispatch(requestActionSucceeded());
          setSearchParams({}, { replace: true });
          requestAnimationFrame(() => {
            cancelTriggerRef.current?.focus();
            cancelTriggerRef.current = null;
          });
          // Note: handledCancelActionRef is intentionally NOT cleared here.
          // Clearing it before searchParams update takes effect causes the
          // deep-link useEffect to re-open the dialog when allLoadedEvents changes.
          // The ref is cleared in handleCancelDismiss or overwritten on next cancel.
        })
        .catch((err: unknown) => {
          const status = (err as { status?: number })?.status;
          const errorMap: Record<number, string> = {
            400: 'Cancellation already requested for this event.',
            403: 'Access denied. Only the event owner can cancel.',
            404: 'Event not found.',
            409: t('dashboard:cancelDialog.alreadyCancelledMessage'),
            412: 'Event was modified. Please refresh and try again.',
          };
          const message =
            (status && errorMap[status]) ||
            (err as Error)?.message ||
            'Failed to cancel request. Please try again.';
          dispatch(requestActionFailed(message));
        });
    },
    [eventToCancel, cancelEvent, setSearchParams, dispatch, t]
  );

  const handleCancelDismiss = useCallback(() => {
    handledCancelActionRef.current = null;
    setCancelDialogOpen(false);
    setCancelTargetEventId(null);
    setDeepLinkedCancelEvent(null);
    dispatch(requestActionSucceeded());
    setSearchParams({}, { replace: true });
    requestAnimationFrame(() => {
      cancelTriggerRef.current?.focus();
      cancelTriggerRef.current = null;
    });
  }, [setSearchParams, dispatch]);

  const handleRetry = useCallback(() => {
    void retryTab(activeTab);
  }, [activeTab, retryTab]);

  const handlePageChange = useCallback(
    (page: number) => {
      const heading = sectionHeadingRef.current;
      const scrollContainer = document.getElementById('app-main');
      if (heading && scrollContainer) {
        const headingTop =
          heading.getBoundingClientRect().top -
          scrollContainer.getBoundingClientRect().top +
          scrollContainer.scrollTop;
        scrollContainer.scrollTo({ top: headingTop, behavior: 'smooth' });
      }
      void setPage(activeTab, page);
    },
    [activeTab, setPage]
  );

  const selectedViewHandler = activeTab === 'draft' ? handleDraftView : handleView;

  return (
    <div className={styles.root}>
      <HomeHero onStartBooking={handleStartBooking} figmaVariant ctaRef={heroCtaRef} />

      <div
        className={mergeClasses(
          styles.contentArea,
          !isHeroCtaVisible && styles.contentAreaWithFloatingBar
        )}
      >
        <section aria-labelledby={sectionHeadingId}>
          <h2 id={sectionHeadingId} className={styles.heading} ref={sectionHeadingRef}>
            {t('homeScreen:homeScreen.section.heading')}
          </h2>
          <p className={styles.subtitle}>{t('homeScreen:homeScreen.section.subtitle')}</p>

          <DashboardTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            draftCount={countForTab('draft')}
            currentCount={countForTab('current')}
            pastCount={countForTab('past')}
            figmaVariant
          />

          <div className={styles.tabContent}>
            {isInitialLoadComplete && !hasEvents && !currentTabState.error ? (
              <WelcomeSection onStartNewRequest={handleStartBooking} />
            ) : (
              <>
                <RequestList
                  events={selectedEvents}
                  loading={currentTabState.loading || !isInitialLoadComplete}
                  error={currentTabState.error ?? undefined}
                  hasMore={false}
                  onView={selectedViewHandler}
                  onDelete={handleDelete}
                  onCancel={handleCancel}
                  tab={activeTab}
                  onCreateRequest={handleStartBooking}
                  onRetry={handleRetry}
                  figmaVariant
                />
                <BookingsPagination
                  currentPage={currentTabState.currentPage}
                  pageCount={currentTabState.pageCount}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </div>

          <DeleteConfirmDialog
            open={deleteDialogOpen}
            eventTitle={eventToDelete?.eventTitle ?? ''}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            loading={deleteLoading}
            error={deleteError}
          />

          <CancelRequestDialog
            open={cancelDialogOpen}
            eventTitle={
              eventToCancel
                ? formatSeriesOrdinalTitle(
                    eventToCancel.eventTitle ?? '',
                    eventToCancel.seriesIndex,
                    eventToCancel.seriesTotal
                  )
                : ''
            }
            onConfirm={handleConfirmCancel}
            onCancel={handleCancelDismiss}
            loading={cancelLoading}
            error={cancelError}
            message={cancelDialogMessage}
          />
        </section>
      </div>

      <EventRequestDetails
        open={selectedEventId !== null}
        eventId={selectedEventId}
        titleOverride={selectedEventTitleOverride}
        onClose={() => {
          dispatch(setSelectedEventId(null));
          setSearchParams({}, { replace: true });
          // Restore focus to the button that opened the dialog
          requestAnimationFrame(() => {
            viewTriggerRef.current?.focus();
            viewTriggerRef.current = null;
          });
        }}
      />

      <FloatingActionBar
        visible={!isHeroCtaVisible}
        hideOnFocusInputs
        bare
        ariaLabel={t('homeScreen:homeScreen.bookAnother.title')}
        rootLeft="var(--app-rail-width, 0px)"
        rootRight="0px"
        innerMaxWidth="98%"
      >
        <BookAnotherCard onAction={handleStartBooking} />
      </FloatingActionBar>
    </div>
  );
};

BookingsScreen.displayName = 'BookingsScreen';
